import {useQueryClient, useSuspenseQuery} from '@github-ui/react-query'
import {createPersister} from '@github-ui/react-query-persister'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {Type} from '@github-ui/react-query-persister/typebox'
import {throttle} from '@github/mini-throttle'
import {useAnalytics} from '@github-ui/use-analytics'
import {useAlive} from '@github-ui/use-alive'
import {reactFetch} from '@github-ui/verified-fetch'
import {useCurrentRepository} from '@github-ui/current-repository'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {useMemo, useCallback, useEffect} from 'react'
import type {CodespacesData} from './codespaces-types'
import {buildCodespacesPath} from '../build-codespaces-path'
import {isCachingEnabled} from './flags'

const CodespaceSchema = Type.Object({
  id: Type.Number(),
  name: Type.String(),
  displayName: Type.String(),
  openUrl: Type.String(),
  branch: Type.String(),
  lastUsedAt: Type.Union([Type.String(), Type.Null()]),
  isActive: Type.Boolean(),
  isFailed: Type.Boolean(),
  isBlocking: Type.Boolean(),
  blockingOperationText: Type.Union([Type.String(), Type.Null()]),
  hasUnpushedChanges: Type.Boolean(),
  hasUncommittedChanges: Type.Boolean(),
  commitsAhead: Type.Number(),
  pullRequest: Type.Union([
    Type.Object({
      number: Type.Number(),
      state: Type.Union([Type.Literal('open'), Type.Literal('closed'), Type.Literal('merged')]),
    }),
    Type.Null(),
  ]),
  isForCurrentRepository: Type.Boolean(),
  isForFork: Type.Boolean(),
  isOwnersFork: Type.Boolean(),
  forkNwo: Type.Union([Type.String(), Type.Null()]),
  autoDeleteSoon: Type.Boolean(),
  retentionExpiresAt: Type.Union([Type.String(), Type.Null()]),
  retentionPeriodDays: Type.Union([Type.Number(), Type.Null()]),
  isUnpublished: Type.Boolean(),
  deletable: Type.Boolean(),
  deleteConfirmationMessage: Type.String(),
  suspendable: Type.Boolean(),
  isReadOnly: Type.Boolean(),
  needsForkToPush: Type.Boolean(),
  needsMachineTypeChange: Type.Boolean(),
  needsBaseImageChange: Type.Boolean(),
  linkDisabled: Type.Boolean(),
  allowChangeMachineType: Type.Boolean(),
  keepDisabled: Type.Boolean(),
  keepEnabled: Type.Boolean(),
  startDisabled: Type.Boolean(),
  paths: Type.Object({
    codespace: Type.String(),
    suspend: Type.String(),
    rename: Type.String(),
    changeMachineType: Type.String(),
    exportControl: Type.String(),
    export: Type.String(),
    exported: Type.String(),
  }),
  exportBranchName: Type.String(),
  exportBranchUrl: Type.Union([Type.String(), Type.Null()]),
  editors: Type.Array(
    Type.Object({
      id: Type.String(),
      label: Type.String(),
      url: Type.String(),
      icon: Type.String(),
      beta: Type.Boolean(),
    }),
  ),
})

const CodespacesDataSchema = Type.Object({
  header: Type.Object({
    createPath: Type.String(),
    newWithOptionsUrl: Type.String(),
    createFormFields: Type.Object({
      repositoryId: Type.Union([Type.Number(), Type.Null()]),
      pullRequestId: Type.Union([Type.Number(), Type.Null()]),
      expectedBillableOwnerId: Type.Union([Type.Number(), Type.Null()]),
      devcontainerPath: Type.Union([Type.String(), Type.Null()]),
      openInDeeplink: Type.Boolean(),
    }),
    canCreate: Type.Boolean(),
    disableOneClickCreation: Type.Boolean(),
    manageUrl: Type.String(),
    configureDevContainerUrl: Type.Union([Type.String(), Type.Null()]),
    prebuildsUrl: Type.Union([Type.String(), Type.Null()]),
    helpUrl: Type.String(),
    billableOwnerName: Type.Union([Type.String(), Type.Null()]),
    shareUrl: Type.Optional(Type.String()),
    createNotice: Type.Optional(
      Type.Union([
        Type.Object({
          message: Type.String(),
          linkText: Type.Optional(Type.String()),
          linkUrl: Type.Optional(Type.String()),
        }),
        Type.Null(),
      ]),
    ),
    maintenanceNotice: Type.Optional(
      Type.Union([
        Type.Object({
          message: Type.String(),
          linkText: Type.String(),
          linkUrl: Type.String(),
          dismissPath: Type.String(),
        }),
        Type.Null(),
      ]),
    ),
  }),
  codespaces: Type.Array(CodespaceSchema),
  pullRequestWarning: Type.Union([
    Type.Object({
      message: Type.String(),
      linkText: Type.Optional(Type.String()),
      linkUrl: Type.Optional(Type.String()),
    }),
    Type.Null(),
  ]),
  showActions: Type.Boolean(),
  aliveChannel: Type.Union([Type.String(), Type.Null()]),
  ssoWarning: Type.Union([
    Type.Object({
      message: Type.String(),
    }),
    Type.Null(),
  ]),
})

const CodespacesDataValidator = Compile(CodespacesDataSchema)

function normalizeCodespacesJsonPath(codespacesPath?: string): string | undefined {
  if (!codespacesPath) return undefined

  const url = new URL(codespacesPath, 'https://github.local')
  url.searchParams.delete('current_branch')

  return `${url.pathname}${url.search}`
}

async function fetchCodespacesData(codespacesPath?: string): Promise<CodespacesData | null> {
  if (!codespacesPath) return null

  const result = await reactFetch(codespacesPath, {
    headers: {Accept: 'application/json'},
  })
  if (result.status >= 400 && result.status <= 499) {
    return null
  } else if (!result.ok) {
    throw new Error(`HTTP ${result.status}`)
  }

  const data = (await result.json()) as CodespacesData
  return data
}

const useCodespacesDataQueryOptions = (codespacesPathProp?: string) => {
  const repo = useCurrentRepository()
  const refInfo = useRefInfo()
  const codespacesPath =
    normalizeCodespacesJsonPath(codespacesPathProp) ||
    (repo && refInfo ? normalizeCodespacesJsonPath(buildCodespacesPath(repo.id, refInfo.name)) : undefined)
  const {sendAnalyticsEvent} = useAnalytics()

  return useMemo(
    () => ({
      queryKey: ['CodespacesTab.json', codespacesPath],
      queryFn: () => fetchCodespacesData(codespacesPath),
      staleTime: 60_000,
      persister: createPersister({
        validator: CodespacesDataValidator,
        sendAnalyticsEvent,
        enabled: isCachingEnabled(),
      }),
    }),
    [codespacesPath, sendAnalyticsEvent],
  )
}

export function useCodespacesData(codespacesPath?: string) {
  const queryOptions = useCodespacesDataQueryOptions(codespacesPath)
  return useSuspenseQuery(queryOptions)
}

export function usePrefetchCodespacesData(codespacesPath?: string) {
  const queryClient = useQueryClient()
  const queryOptions = useCodespacesDataQueryOptions(codespacesPath)

  return useCallback(() => {
    const isEnabled = isCachingEnabled()
    if (!isEnabled) return
    return queryClient.prefetchQuery(queryOptions)
  }, [queryClient, queryOptions])
}

export function useInvalidateCodespacesData(codespacesPath?: string) {
  const queryClient = useQueryClient()
  const queryOptions = useCodespacesDataQueryOptions(codespacesPath)

  return useCallback(() => {
    return queryClient.invalidateQueries({queryKey: queryOptions.queryKey})
  }, [queryClient, queryOptions.queryKey])
}

const ALIVE_THROTTLE_MS = 2000

export function useCodespacesAlive(aliveChannel: string | null | undefined, codespacesPath?: string) {
  const invalidate = useInvalidateCodespacesData(codespacesPath)
  const throttledInvalidate = useMemo(() => throttle(invalidate, ALIVE_THROTTLE_MS), [invalidate])

  useEffect(() => {
    return () => throttledInvalidate.cancel()
  }, [throttledInvalidate])

  useAlive(aliveChannel, throttledInvalidate)
}
